import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Lock } from 'lucide-react';

import { Button } from 'shared-ui';

import { clientHasModule, firstAvailableProduct, PRODUCT_MODULE } from '@/clients/modules';
import { useClient } from '@/clients/useClient';
import { UpsellDialog } from './UpsellDialog';
import { UPSELL_PRODUCTS, type ProductKey } from './upsellContent';

interface ProductGuardProps {
    /** Which product this route is. Drives the required module and the upsell content. */
    product: ProductKey;
    children: ReactNode;
}

/**
 * Gates a product (e.g. SAVI, Find &amp; Fix) on the active client having the required module.
 *
 * When the active client has the module, the product renders normally. Otherwise the product is
 * NOT mounted (so its data hooks never fire — avoiding 403s against the backend module gate).
 *
 * Reactive to the active client. When the client *changes* (e.g. an Origin user switching client)
 * into a product they don't have, we auto-navigate to the first product the new client DOES have —
 * popping an upsell on switch is jarring. The upsell is only shown when the user navigates directly
 * to an unavailable product (client unchanged) or the client has no available product at all.
 *
 * The app shell (sidebar + client switcher) lives in RootLayout above this guard, so the user can
 * still switch client or product while the upsell is shown.
 */
export function ProductGuard({ product, children }: ProductGuardProps) {
    const { selectedClient } = useClient();
    const navigate = useNavigate();
    const hasAccess = clientHasModule(selectedClient, PRODUCT_MODULE[product]);

    const clientId = selectedClient?.id;
    // Previously-seen client, used to tell a *client switch* apart from direct navigation. We only
    // commit the new client once we've decided NOT to redirect (see effect), so that while a redirect
    // is pending the guard keeps rendering nothing instead of flashing the upsell.
    const [prevClientId, setPrevClientId] = useState(clientId);
    // A *switch* is a move between two real clients. The initial null → resolvedClient transition
    // during bootstrap is NOT a switch (otherwise a cold direct navigation would auto-redirect
    // instead of rendering the page / showing the upsell as intended).
    const clientChanged = prevClientId != null && clientId != null && prevClientId !== clientId;

    // On a client change into an unavailable product, redirect to another enabled product if one exists.
    const redirectTo = !hasAccess && clientChanged ? firstAvailableProduct(selectedClient)?.to : undefined;

    useEffect(() => {
        if (redirectTo) {
            void navigate({ to: redirectTo });
        } else if (prevClientId !== clientId) {
            // eslint-disable-next-line react-hooks/set-state-in-effect, react-x/set-state-in-effect
            setPrevClientId(clientId);
        }
    }, [clientId, prevClientId, redirectTo, navigate]);

    if (hasAccess) return <>{children}</>;
    // Still bootstrapping — the app-level loader covers this; avoid flashing the upsell.
    if (selectedClient == null) return null;
    // Redirecting to an available product — render nothing rather than the upsell.
    if (redirectTo) return null;
    // Key on the client so the upsell re-opens when switching between clients that both lack the module.
    return <ProductUpsell key={selectedClient.id} product={product} />;
}

function ProductUpsell({ product }: { product: ProductKey }) {
    const [open, setOpen] = useState(true);
    const { name } = UPSELL_PRODUCTS[product];

    return (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6 text-center">
            <div className="bg-muted text-muted-foreground flex size-14 items-center justify-center rounded-full">
                <Lock className="size-6" />
            </div>
            <div className="space-y-1">
                <p className="text-foreground text-lg font-black uppercase">{name}</p>
                <p className="text-muted-foreground max-w-sm text-sm leading-5">
                    This client&apos;s plan doesn&apos;t include {name}.
                </p>
            </div>
            <Button
                variant="outline"
                className="h-10"
                onClick={() => {
                    setOpen(true);
                }}
            >
                Learn more
            </Button>
            <UpsellDialog open={open} onOpenChange={setOpen} product={product} />
        </div>
    );
}
